import { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import { Header } from 'app/KinerjaShared/components/Header'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import { IndikatorPraktikKinerjaCard } from 'app/KinerjaShared/components/IndikatorPraktikKinerjaCard'
import {
  ObservasiStepCard,
  PelaksanaanTindakLanjutStepCard,
  PersiapanStepCard,
  RencanaTindakLanjutStepCard,
  RefleksiEvaluasiStepCard,
} from 'app/KinerjaShared/components/PraktikKinerjaPegawaiStepCards'
import {
  useAPIPelaksanaanProgress,
  useAPIPelaksanaanTargetPerilaku,
} from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
} from 'app/KinerjaShared/components/SectionCard'
import { PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE } from 'app/KinerjaPamongPage/constants/praktik-kinerja'
import {
  PRAKTIK_KINERJA_STEPS,
  PRAKTIK_KINERJA_STEP_ORDER,
  PRAKTIK_KINERJA_RESETABLE_STEP_ORDER,
} from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  TargetPerilakuConfirmationDialog,
  RequestDialog,
  RollbackDialog,
  StepCard,
} from 'app/KinerjaShared/components/EditPraktikKinerjaDialogs'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
} from 'api/kinerja/useAPIKinerja'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type {
  TFollowUpEvaluationDocumentStates,
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'
import type { MultiPeriodPayload } from 'types/kinerja'

type EditPraktikKinerjaDialogType =
  | 'ROLLBACK'
  | 'TARGET_PERILAKU_CONFIRMATION'
  | 'APPEAL'
  | 'NONE'

type HubPraktikKinerjaProps = {
  periodParams?: MultiPeriodPayload
}

export function HubPraktikKinerja({ periodParams }: HubPraktikKinerjaProps) {
  const { periodNo, periodYear } = usePeriodContext()
  const queryClient = useQueryClient()

  const { tipe } = useKinerjaTipe()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const {
    isLoading: isPelaksanaanProgressLoading,
    data: pelaksanaanProgressData,
  } = useAPIPelaksanaanProgress({
    tipe,
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })
  const contentId = pelaksanaanProgressData?.data?.contentId

  const progressData = useMemo(() => {
    if (!pelaksanaanProgressData?.data)
      return {
        step: undefined,
        latestFormState: undefined,
        appealStep: undefined,
      }

    return {
      step: pelaksanaanProgressData.data.state,
      latestFormState: pelaksanaanProgressData.data.latestFormState,
      appealStep: pelaksanaanProgressData.data.appealState,
    }
  }, [pelaksanaanProgressData?.data])

  // -----------------------------------------
  // Get target perilaku data for rubrik dialog
  // -----------------------------------------
  const { isLoading: isTargetPerilakuLoading, data: targetPerilakuData } =
    useAPIPelaksanaanTargetPerilaku(contentId, {
      enabled: !isPelaksanaanProgressLoading && !!contentId,
      staleTime: 30 * 60 * 1000,
    })

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      PAMONG_PRAKTIK_KINERJA_INDIKATOR_IMAGE[
        pelaksanaanProgressData?.data?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.data?.subIndicatorName])

  // -----------------------------------------
  // Check if pelaksanaan is completed
  // -----------------------------------------
  const isPelaksanaanCompleted = useMemo(() => {
    const currentStepIndex =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(progressData.step) ?? 0
    const minimalStepToComplete = 'REFLECTION_FOLLOW_UP_EVALUATION'
    const minimalStepToCompleteIndex =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(minimalStepToComplete) ?? Infinity

    const isCompleted = currentStepIndex >= minimalStepToCompleteIndex

    return isCompleted
  }, [progressData.step])

  // -----------------------------------------
  // Handle edit Praktik Kinerja actions
  // -----------------------------------------
  const [activeEditDialog, setActiveEditDialog] = useState<{
    step: TStatePelaksanaan
    type: EditPraktikKinerjaDialogType
  }>({ step: undefined, type: 'NONE' })

  const handleCloseEditDialog = () => {
    setActiveEditDialog({ step: undefined, type: 'NONE' })
  }

  const handleOpenEditDialog = (step: TStatePelaksanaan) => {
    setActiveEditDialog({
      step,
      type: resolveEditDialogByStep(
        step,
        progressData.step,
        progressData.latestFormState
      ),
    })
  }

  // -----------------------------------------
  // Handle safe back navigation
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH,
  })

  return (
    <>
      <main className="px-8 pt-12 pb-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <Header
              title="Praktik Kinerja"
              className="mb-9"
              onBack={handleBack}
            />
          </div>

          {isPelaksanaanCompleted && <CompletedPelaksanaanBanner />}

          <SectionCard>
            <SectionCardHeader variant="gradient-blue">
              <Text as="h2" color="inherit" variant="heading-lg">
                Periode:{' '}
                <CardPeriod
                  period={resolvedPeriodParams?.skpPeriod}
                  year={resolvedPeriodParams?.skpYear}
                  enableMultiplePeriodSupport
                />
              </Text>
            </SectionCardHeader>
            <SectionCardBody className="p-6 space-y-12">
              <IndikatorPraktikKinerjaCard
                illustrationPath={illustrationAssetPath}
                title={pelaksanaanProgressData?.data?.subIndicatorName}
                targetKualitas={pelaksanaanProgressData?.data?.qualityTarget}
                targetPerilakuList={
                  targetPerilakuData?.data?.map((item) => ({
                    id: item.id,
                    targetPerilaku: item.behavioralTarget,
                    perilakuDianjurkanList: item.recommendedBehaviors,
                    perilakuDilarangList: item.prohibitedBehaviors,
                  })) || []
                }
                isLoading={
                  isPelaksanaanProgressLoading || isTargetPerilakuLoading
                }
              />

              <div className="space-y-14">
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 1: Pelaksanaan Observasi
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PersiapanStepCard
                      className="max-w-96"
                      progressData={progressData}
                      sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
                      step="DOCUMENT_PREPARATION"
                      title={
                        <>
                          Pemantauan Kinerja: <br /> Isi{' '}
                          {PRAKTIK_KINERJA_STEPS.DOCUMENT_PREPARATION.title}
                        </>
                      }
                      imagePath={
                        PRAKTIK_KINERJA_STEPS.DOCUMENT_PREPARATION.imagePath
                      }
                      nextPath={PRAKTIK_KINERJA_STEPS.DOCUMENT_PREPARATION.getPath()}
                      description="Diskusi dengan Kepala SKB untuk target perilaku, upaya mempelajari, dan jadwal."
                      onClickEdit={handleOpenEditDialog}
                    />
                    <StepArrow />
                    <ObservasiStepCard
                      className="max-w-96"
                      description="Observasi akan dilaksanakan sesuai jadwal"
                      imagePath={
                        PRAKTIK_KINERJA_STEPS.OBSERVATION_IMPLEMENTATION
                          .imagePath
                      }
                      nextPath={PRAKTIK_KINERJA_STEPS.OBSERVATION_IMPLEMENTATION.getPath()}
                      progressData={progressData}
                      sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
                      step="OBSERVATION_IMPLEMENTATION"
                      title={
                        <>
                          Pemantauan Kinerja: <br /> Observasi Praktik Kinerja
                        </>
                      }
                    />
                    <StepArrow />
                    <RencanaTindakLanjutStepCard
                      className="max-w-96"
                      description="Refleksi dan diskusi dengan Kepala SKB untuk isi dukungan yang dibutuhkan."
                      detailHref={
                        progressData.step === 'DOCUMENT_FOLLOW_UP'
                          ? `${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja/pre-rencana-tindak-lanjut`
                          : PRAKTIK_KINERJA_STEPS.DOCUMENT_FOLLOW_UP.getPath()
                      }
                      imagePath={
                        PRAKTIK_KINERJA_STEPS.DOCUMENT_FOLLOW_UP.imagePath
                      }
                      lockedHint="Menunggu aksi dari Kepala SKB."
                      progressData={progressData}
                      sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
                      step="DOCUMENT_FOLLOW_UP"
                      title={
                        <>
                          Pembinaan Kinerja:
                          <br />
                          Isi {PRAKTIK_KINERJA_STEPS.DOCUMENT_FOLLOW_UP.title}
                        </>
                      }
                      onClickEdit={handleOpenEditDialog}
                    />
                  </div>
                </div>
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 2: Pelaksanaan Tindak Lanjut
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PelaksanaanTindakLanjutStepCard
                      className="max-w-96"
                      description="Laksanakan Pilihan Belajar dan tindak lanjut lainnya."
                      imagePath="/illustrations/img-smartphone-pop-up-modal-cart-section.png"
                      progressData={progressData}
                      sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
                      step="DOCUMENT_REFLECTION_FOLLOW_UP"
                      title={
                        <>
                          Pembinaan Kinerja: <br /> Pelaksanaan Tindak Lanjut
                        </>
                      }
                    />
                    <StepArrow />
                    <RefleksiEvaluasiStepCard
                      className="max-w-96"
                      description="Diskusi dengan Kepala SKB untuk isi capaian, tantangan, dan upaya perbaikan."
                      detailHref={PRAKTIK_KINERJA_STEPS.DOCUMENT_REFLECTION_FOLLOW_UP.getPath()}
                      imagePath={
                        PRAKTIK_KINERJA_STEPS.DOCUMENT_REFLECTION_FOLLOW_UP
                          .imagePath
                      }
                      lockedHint="Menunggu aksi dari Kepala SKB."
                      progressData={progressData}
                      sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
                      step="DOCUMENT_REFLECTION_FOLLOW_UP"
                      title={
                        <>
                          Pembinaan Kinerja: <br /> Isi{' '}
                          {
                            PRAKTIK_KINERJA_STEPS.DOCUMENT_REFLECTION_FOLLOW_UP
                              .title
                          }
                        </>
                      }
                      onClickEdit={handleOpenEditDialog}
                    />
                  </div>
                </div>
              </div>
            </SectionCardBody>
          </SectionCard>
        </div>
      </main>

      <RollbackDialog
        isOpen={activeEditDialog.type === 'ROLLBACK' && !!activeEditDialog.step}
        onClose={() => setActiveEditDialog({ step: undefined, type: 'NONE' })}
        onCancel={() => setActiveEditDialog({ step: undefined, type: 'NONE' })}
        step={activeEditDialog.step as TPelaksanaanAppealableStep}
        url={PRAKTIK_KINERJA_STEPS[activeEditDialog.step]?.getPath?.()}
        onSuccess={() => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })
          queryClient.invalidateQueries(
            API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
            {
              refetchActive: false,
              refetchInactive: false,
            }
          )
        }}
      />

      <TargetPerilakuConfirmationDialog
        isOpen={
          activeEditDialog.type === 'TARGET_PERILAKU_CONFIRMATION' &&
          !!activeEditDialog.step
        }
        onClose={handleCloseEditDialog}
        onCancel={handleCloseEditDialog}
        onConfirm={() =>
          setActiveEditDialog({ step: activeEditDialog.step, type: 'APPEAL' })
        }
      />

      <RequestDialog
        title="Ajukan perubahan ke Kepala SKB. Berikut hal yang terdampak:"
        isOpen={activeEditDialog.type === 'APPEAL' && !!activeEditDialog.step}
        sortedSteps={PRAKTIK_KINERJA_STEP_ORDER}
        sortedResetableSteps={PRAKTIK_KINERJA_RESETABLE_STEP_ORDER}
        progressStep={progressData.step}
        appealStep={activeEditDialog.step as TPelaksanaanAppealableStep}
        onClose={handleCloseEditDialog}
        renderStepCard={(step) => {
          const stepData = PRAKTIK_KINERJA_STEPS[step]

          return (
            <StepCard
              title={stepData.title}
              description={stepData.resetMessage.pegawai}
              imagePath={stepData.imagePath}
              className="max-w-md"
              key={step}
            />
          )
        }}
      />
    </>
  )
}

function StepArrow() {
  return (
    <div className="flex items-center">
      <Icon>arrow_forward</Icon>
    </div>
  )
}

function CompletedPelaksanaanBanner() {
  return (
    <section className="mb-6 bg-white rounded-lg shadow-card">
      <div className="flex items-center p-6 space-x-6">
        <img
          height={84}
          width={84}
          src={assetUrl('/illustrations/img-thumbs-up.png')}
          className="flex-shrink-0"
          alt=""
        />
        <div className="items-center flex-1 space-y-2">
          <Text variant="heading-md" as="h2">
            Pelaksanaan Praktik Kinerja selesai!
          </Text>
          <Text color="subdued">
            Anda dapat lanjut cek kegiatan lainnya melalui menu{' '}
            <strong>Sebagai Pegawai</strong>.
          </Text>
        </div>
      </div>
    </section>
  )
}

const EVALUATED_PRAKTIK_KINERJA_EDIT_DIALOG_MAPPING: Partial<
  Record<TStatePelaksanaan, EditPraktikKinerjaDialogType>
> = {
  OBSERVATION_IMPLEMENTATION: 'TARGET_PERILAKU_CONFIRMATION',
  FOLLOW_UP_EVALUATION: 'APPEAL',
  REFLECTION_FOLLOW_UP_EVALUATION: 'APPEAL',
}

const PRAKTIK_KINERJA_EVALUATION_STEP_MAPPING: Partial<
  Record<TStatePelaksanaan, TStatePelaksanaan>
> = {
  DOCUMENT_PREPARATION: 'OBSERVATION_IMPLEMENTATION',
  DOCUMENT_FOLLOW_UP: 'FOLLOW_UP_EVALUATION',
  DOCUMENT_REFLECTION_FOLLOW_UP: 'REFLECTION_FOLLOW_UP_EVALUATION',
}

function resolveEditDialogByStep(
  step: TStatePelaksanaan,
  progressState: TStatePelaksanaan,
  latestFormState: TFollowUpEvaluationDocumentStates
): EditPraktikKinerjaDialogType {
  const evaluationStep = PRAKTIK_KINERJA_EVALUATION_STEP_MAPPING[step]

  // Do rollback if the current step is not evaluated yet
  if (evaluationStep === progressState && latestFormState === 'NOT_CREATED') {
    return 'ROLLBACK'
  }

  // Otherwise, show the appropriate dialog based on the step
  return EVALUATED_PRAKTIK_KINERJA_EDIT_DIALOG_MAPPING[evaluationStep] ?? 'NONE'
}
